import { ColorMatrixFilter, Container } from "pixi.js";
import { PROC_TILE_PX } from "./proceduralPatterns.js";
import { createFoilMesh, type FoilMeshController } from "./renderers/foilMesh.js";
import { createPatternMesh, type PatternMeshController } from "./renderers/patternMesh.js";
import type { Finish, SkinSpec } from "./spec.js";
import { PATTERN_TILE, type SkinAssets } from "./textures.js";

export interface Axes {
  pattern: boolean;
  tint: boolean;
  finish: boolean;
}

const ALL_AXES: Axes = { pattern: true, tint: true, finish: true };

export interface SkinnedCardOptions {
  /** The base card primitive to wrap. SkinnedCard does not own its lifecycle. */
  base: Container;
  /** Local-space size of the base, used to size the pattern overlay. */
  baseWidth: number;
  baseHeight: number;
  assets: SkinAssets;
}

/**
 * Wraps a base card Container (typically `CardView`) with cosmetic effects:
 * a pattern Mesh (color + height + gloss bundle, lit per-pixel), a foil
 * Mesh layered on top via screen blend (foil/chrome/holographic finish),
 * and a tint ColorMatrix filter applied to the whole skin target.
 *
 * Every cosmetic axis lives on SkinSpec — there is no parallel tunables
 * path. Pass `null` to render the bare base.
 */
export class SkinnedCard extends Container {
  readonly base: Container;
  private readonly skinTarget: Container;
  private readonly patternCtrl: PatternMeshController;
  private readonly foilCtrl: FoilMeshController;
  private readonly tintFilter: ColorMatrixFilter;
  private readonly assets: SkinAssets;
  private readonly baseWidth: number;
  private readonly baseHeight: number;
  private spec: SkinSpec | null = null;
  private axes: Axes = { ...ALL_AXES };
  private currentBundleIndex = -1;

  constructor(options: SkinnedCardOptions) {
    super();
    this.base = options.base;
    this.baseWidth = options.baseWidth;
    this.baseHeight = options.baseHeight;
    this.skinTarget = resolveSkinTarget(options.base);
    this.assets = options.assets;
    this.addChild(this.base);

    const fallback = options.assets.patterns[0];
    if (!fallback) throw new Error("SkinnedCard: assets.patterns is empty");

    this.patternCtrl = createPatternMesh(
      fallback,
      options.assets.scratchMap,
      options.baseWidth,
      options.baseHeight,
    );
    this.patternCtrl.view.visible = false;
    this.skinTarget.addChild(this.patternCtrl.view);

    this.foilCtrl = createFoilMesh(
      fallback,
      options.assets.scratchMap,
      options.baseWidth,
      options.baseHeight,
    );
    this.foilCtrl.view.visible = false;
    this.skinTarget.addChild(this.foilCtrl.view);

    this.tintFilter = new ColorMatrixFilter();

    // Sync each mesh's own groupAlpha into its uAlpha uniform every
    // render tick — Pixi only auto-multiplies alpha for batched
    // materials, custom GlPrograms have to thread it manually.
    this.patternCtrl.view.onRender = () => {
      this.patternCtrl.setAlpha(this.patternCtrl.view.groupAlpha);
    };
    this.foilCtrl.view.onRender = () => {
      this.foilCtrl.setAlpha(this.foilCtrl.view.groupAlpha);
    };
  }

  applySkin(spec: SkinSpec | null, axes: Axes = ALL_AXES): void {
    this.spec = spec;
    this.axes = { ...axes };

    if (!spec) {
      this.patternCtrl.view.visible = false;
      this.foilCtrl.view.visible = false;
      this.patternCtrl.view.filters = [];
      this.skinTarget.filters = [];
      return;
    }

    this.applyFoilTunables(spec);

    const idx = spec.pattern.index % this.assets.patterns.length;
    const bundle = this.assets.patterns[idx] ?? this.assets.patterns[0];
    if (bundle && idx !== this.currentBundleIndex) {
      this.patternCtrl.setBundle(bundle);
      this.foilCtrl.setBundle(bundle);
      this.currentBundleIndex = idx;
    }

    const cwIdx = spec.colorway % this.assets.colorways.length;
    const colorway = this.assets.colorways[cwIdx] ?? this.assets.colorways[0];
    if (colorway) this.patternCtrl.setColorway(colorway.palette);

    const bgIdx = spec.cardBackground % this.assets.cardBackgrounds.length;
    const bg = this.assets.cardBackgrounds[bgIdx] ?? this.assets.cardBackgrounds[0];
    if (bg) this.patternCtrl.setCardBackground(bg.color);

    if (axes.pattern) {
      this.patternCtrl.view.visible = true;
      this.refreshPatternLook();
    } else {
      this.patternCtrl.view.visible = false;
    }

    const finishActive = axes.finish && spec.finish !== "matte";
    if (finishActive) {
      this.foilCtrl.view.visible = true;
      this.refreshFoilLook();
    } else {
      this.foilCtrl.view.visible = false;
    }

    if (axes.tint) {
      this.tintFilter.reset();
      this.tintFilter.hue(spec.tint.hue * 180, false);
      this.tintFilter.saturate(spec.tint.saturation - 1, true);
      this.tintFilter.brightness(spec.tint.brightness, true);
      this.patternCtrl.view.filters = [this.tintFilter];
    } else {
      this.patternCtrl.view.filters = [];
    }
    this.skinTarget.filters = [];
  }

  private applyFoilTunables(spec: SkinSpec): void {
    // Finish strength channels into whichever foil track the current
    // finish uses. Holographic and metals are mutually exclusive at
    // render time so it's safe to zero the other channel.
    const metal = spec.finish === "holographic" ? 0 : spec.finishStrength;
    const holo = spec.finish === "holographic" ? spec.finishStrength : 0;
    this.foilCtrl.setTunables({
      metalStrength: metal,
      holoStrength: holo,
      coverageBias: spec.coverageBias,
    });
    const cell = Math.max(1, spec.cellSize);
    this.foilCtrl.setPixelGrid(this.baseWidth / cell, this.baseHeight / cell);
  }

  private refreshPatternLook(): void {
    if (!this.spec) return;
    const tileWorldSize = PATTERN_TILE * this.spec.pattern.scale;
    const tilesAcross = this.baseWidth / tileWorldSize;
    const tilesDown = this.baseHeight / tileWorldSize;
    const baseDepth = depthForFinish(this.spec.finish);
    const depth = clamp01(baseDepth * this.spec.depth);
    this.patternCtrl.setLook({
      tileScaleX: tilesAcross,
      tileScaleY: tilesDown,
      tileOffsetX: this.spec.pattern.offsetX,
      tileOffsetY: this.spec.pattern.offsetY,
      overlayAlpha: this.spec.pattern.overlayAlpha,
      bumpScale: 2.0,
      texelSize: 1 / PROC_TILE_PX,
      viewTiltX: this.skew.x,
      viewTiltY: this.skew.y,
      wear: this.spec.wear,
      depth,
    });
  }

  private refreshFoilLook(): void {
    if (!this.spec) return;
    const tileWorldSize = PATTERN_TILE * this.spec.pattern.scale;
    const tilesAcross = this.baseWidth / tileWorldSize;
    const tilesDown = this.baseHeight / tileWorldSize;
    this.foilCtrl.setLook({
      finish: finishToFloat(this.spec.finish),
      seed: (this.spec.pattern.offsetX + this.spec.pattern.offsetY) * 0.5,
      tileScaleX: tilesAcross,
      tileScaleY: tilesDown,
      tileOffsetX: this.spec.pattern.offsetX,
      tileOffsetY: this.spec.pattern.offsetY,
      viewTiltX: this.skew.x,
      viewTiltY: this.skew.y,
      wear: this.spec.wear,
    });
  }

  refreshTilt(): void {
    if (!this.spec) return;
    if (this.axes.pattern) this.refreshPatternLook();
    if (this.axes.finish && this.spec.finish !== "matte") this.refreshFoilLook();
  }
}

function finishToFloat(finish: Finish): number {
  switch (finish) {
    case "matte":
      return 0;
    case "silver":
      return 1;
    case "gold":
      return 2;
    case "bronze":
      return 3;
    case "holographic":
      return 4;
  }
}

function depthForFinish(finish: Finish): number {
  switch (finish) {
    case "matte":
      return 0.12;
    case "silver":
      return 0.62;
    case "gold":
      return 0.7;
    case "bronze":
      return 0.65;
    case "holographic":
      return 0.92;
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function resolveSkinTarget(base: Container): Container {
  const candidate = (base as { skinLayer?: unknown }).skinLayer;
  if (candidate instanceof Container) return candidate;
  return base;
}
